// eslint-disable-next-line import/no-extraneous-dependencies
import React from 'react';
import { Background, ContentWrapper, FlexContent, OverflowWrapper, BodyWrapper } from './styled';
import { ChecklistContainer, ChecklistProgressBar } from '../../index';
import { type ChecklistContainerProps } from '../checklist-container/ChecklistContainer';
import ChecklistBackground from './ChecklistBackground';
import ChecklistFooter, { type ChecklistFooterProps } from './ChecklistFooter';
import ChecklistHeader from './ChecklistHeader';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { CHECKLIST_TITLE_ID } from '../../common/constants';

export interface ChecklistProps {
	footer?: ChecklistFooterProps;
	header?: React.ReactNode;
	isOpen: boolean;
	onClose: (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	postTasksComponent?: React.ReactNode;
	progressBarAriaLabel?: string;
	shouldFocus?: boolean;
	title: string;
}

const checklistTitleContainerStyles = xcss({
	paddingRight: 'space.250',
	paddingLeft: 'space.250',
	marginTop: 'space.0',
	marginBottom: 'space.200',
	textAlign: 'center',
});

export default (props: ChecklistProps & ChecklistContainerProps) => {
	const { title, isOpen, onClose, footer, shouldFocus } = props;
	const { tasks, onTaskExpand, expandedTask } = props;
	const { header, postTasksComponent, progressBarAriaLabel } = props;

	const completedTasks: number = tasks.reduce((acc, task) => {
		return task.completed ? acc + 1 : acc;
	}, 0);

	const percentCompleted: number = completedTasks / tasks.length;
	const completed: boolean = percentCompleted === 1;

	const ChecklistTitle = ({ children }: { children?: React.ReactNode }) => (
		<Box xcss={checklistTitleContainerStyles} id={CHECKLIST_TITLE_ID}>
			<Heading color="color.text.inverse" size="large" as="h2">
				{children}
			</Heading>
		</Box>
	);

	return (
		<>
			{isOpen && (
				<OverflowWrapper>
					<FlexContent>
						<Background done={completed}>
							<ChecklistBackground />
						</Background>
						<ContentWrapper>
							<ChecklistHeader header={header} onClose={onClose} shouldFocus={shouldFocus} />
							<ChecklistTitle>{title}</ChecklistTitle>
							<BodyWrapper>
								{!completed && (
									<ChecklistProgressBar
										value={percentCompleted}
										ariaLabel={progressBarAriaLabel || title}
									/>
								)}
								<ChecklistContainer
									tasks={tasks}
									expandedTask={expandedTask}
									onTaskExpand={onTaskExpand}
								/>
								{postTasksComponent && postTasksComponent}
							</BodyWrapper>
						</ContentWrapper>
					</FlexContent>
					{footer && <ChecklistFooter {...footer} />}
				</OverflowWrapper>
			)}
		</>
	);
};
